import BackendApiInterface, { QUERY_PARAM_SESSION_ID } from "../backend-api-interface";
import _ from "lodash";
import { sleep } from "../../util/sleep";
import NoAuthPage from "../../pages/auth/NoAuthPage";
import { Dispatch } from "@reduxjs/toolkit";
import { fetchClientList } from "../../store/reducers";
import { MVisionAppClient } from "../../store/mvision-client-list";
import loadExternalScript from "../../util/load-external-script";
import { DISPLAY_VERSION, getSessionId, getDefaultAppName } from "../../environments";

// cockpit already appends http://localhost into this url
const apiRoot = '/config';
const getLicenseUrl = apiRoot + '/get-licenses';
const saveLicenseUrl = apiRoot + '/save-license';
const getModelCustomizationUrl = apiRoot + '/get-segmentation-config';
const saveModelCustomizationUrl = apiRoot + '/save-segmentation-config';
const deleteModelCustomizationUrl = apiRoot + '/delete-segmentation-config';
const getAllDaemonConfigsUrl = apiRoot + '/get-daemon-configs';
const getDaemonConfigUrl = apiRoot + '/get-daemon-config';
const saveDaemonConfigUrl = apiRoot + '/save-daemon-config';
const resetDaemonConfigUrl = apiRoot + '/reset-daemon-config';
const deleteDaemonConfigUrl = apiRoot + '/delete-daemon-config';
const getLabelingUrl = apiRoot + '/labeling';
const getClientListUrl = apiRoot + '/get-user-list';
const getListActiveModelsUrl = apiRoot + '/list-active-models';

const maxRetryAttempts = 64;
const retryWaitInMilliseconds = 250;
const maxInitializeWaitAttempts = 64;

let isInitializing = false;
let isInitialized = false;
let cockpitManifest: any = null;

const defaultPort = 80;
const defaultEnvironmentTag = 'default';

const BACKEND_RESPONSE_OK = '"OK"';

/**
 * Wrapper for accessing Config UI functions through Cockpit API.
 * IMPORTANT: Accessing this class will crash if cockpit is not loaded into environment!
 */
export default class CockpitApi implements BackendApiInterface {

    public init(dispatch: Dispatch) {
        // load cockpit API scripts if we're in a cockpit environment
        loadExternalScript('cockpit', '../base1/cockpit.js');

        // we don't need to do login here, but we do need to fetch the client list
        dispatch(fetchClientList());

        if (!isInitialized && !isInitializing) {
            isInitializing = true;

            fetch('./manifest.json')
                .then(response => {
                    if (response.status === 200) {
                        response.json().then((json: any) => {
                            cockpitManifest = json;
                            isInitialized = true;
                            isInitializing = false;
                        });
                    } else {
                        throw new Error('Failed downloading manifest.json, cannot initialize application');
                    }
                });
        }
    }

    private getErrorMessageFromError(obj: any): string {
        return _.has(obj, 'detail') ? `An error was received from backend: ${obj.detail}`
            : _.has(obj, 'message') ? `An error was received from backend: ${obj.message}`
                : 'Received no valid response from backend.';
    }

    private async getHttpClient(): Promise<any> {
        // see https://cockpit-project.org/guide/latest/cockpit-http.html

        // we might have the wrong port if manifest.json hasn't loaded in yet
        // wait a bit if we're still initializing before settling for default port
        let waitAttempt = 0;
        while (isInitializing && !isInitialized && waitAttempt < maxInitializeWaitAttempts) {
            await sleep(retryWaitInMilliseconds);
            waitAttempt++;
        }

        if (!isInitialized) {
            throw new Error('Cockpit is not yet initialized, cannot get Cockpit HTTP client.');
        }

        let client = null;
        const port = cockpitManifest?.backendPort ?? defaultPort;

        let attempt = 0;
        while (client === null && attempt < maxRetryAttempts) {
            attempt++;
            try {
                client = (window as any).cockpit.http(port);
            }
            catch (err) {
                if (err instanceof TypeError && (err as TypeError).message === 'window.cockpit.http is not a function') {
                    // wait a bit for the script to load and try again
                    await sleep(retryWaitInMilliseconds);
                }
                else {
                    console.log(err);
                    throw err;
                }
            }
        }

        if (client === null) {
            throw new Error('Could not load Cockpit HTTP client.');
        }

        return client;
    }

    private async doLocalCockpitHttpGet(cockpitClient: any, apiUrl: string, headers?: any) {
        let response = null;
        let errorMessage: string | null = null;
        let responseOk = false;

        // set compulsory mvision api headers
        let httpHeaders = headers || {};
        httpHeaders['appVersion'] = _.get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = _.get(httpHeaders, 'user-agent', this.getAppVersion());

        // append client ID to query parameters
        let queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        try {
            response = await cockpitClient.get(apiUrl, queryParams, httpHeaders);
            responseOk = true;
        }
        catch (ex) {
            responseOk = false;
            if (ex && _.get(ex, 'problem', '') === 'not-found') {
                errorMessage = 'Could not connect to backend.';
            } else {
                console.error(ex);
                errorMessage = this.getErrorMessageFromError(ex);
            }
        }

        return { responseOk, response, errorMessage };
    }

    private async getAndParseJsonResponse(apiUrl: string, appClient: MVisionAppClient | undefined, headers?: any, maxRetries: number = 0): Promise<any> {
        const client = await this.getHttpClient();
        let errorMessage: string | null = null;
        let response = null;
        let currentRetry = 0;
        let responseOk = false;

        // set compulsory mvision api headers
        let httpHeaders: any = headers ? headers : {};
        if (appClient) { httpHeaders['userId'] = _.get(httpHeaders, 'userId', appClient.userId); }
        httpHeaders['appVersion'] = _.get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = this.getAppVersion();

        while (!responseOk && currentRetry <= maxRetries) {
            const apiCallResult = await this.doLocalCockpitHttpGet(client, apiUrl, httpHeaders);
            errorMessage = apiCallResult.errorMessage;
            responseOk = apiCallResult.responseOk;

            if (!apiCallResult.responseOk) {
                console.log(`Call to ${apiUrl} failed on retry #${currentRetry}/${maxRetries}`);
                await sleep(retryWaitInMilliseconds);
            }
            else {
                response = apiCallResult.response;
            }

            currentRetry++;
        }

        if (errorMessage) {
            throw new Error(errorMessage);
        }
        if (!response) {
            const message = `Received no valid response from backend.`;
            console.error(message);
            console.log(response);
            throw new Error(message);
        }

        return JSON.parse(response);
    }

    private async postJson(url: string, appClient: MVisionAppClient, headers?: any, json?: string): Promise<boolean> {
        const client = await this.getHttpClient();

        // set compulsory mvision api headers
        let httpHeaders: any = headers ? headers : {};
        httpHeaders['appVersion'] = _.get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = this.getAppVersion();
        httpHeaders['userId'] = _.get(httpHeaders, 'userId', appClient.userId);

        // append client ID to query parameters
        let queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        let response: any;

        if (json !== undefined) {
            response = await client.request({
                path: url,
                body: json,
                method: 'POST',
                params: queryParams,
                headers: httpHeaders,
            });
        } else {
            response = await client.request({
                path: url,
                method: 'POST',
                params: queryParams,
                headers: httpHeaders,
            }).input();
        }

        if (!response) {
            console.error(response);
            throw new Error(`Received no valid response from backend`);
        }

        return response === BACKEND_RESPONSE_OK;
    }

    private async delete(url: string, appClient: MVisionAppClient, headers?: any): Promise<boolean> {
        const client = await this.getHttpClient();

        // set compulsory mvision api headers
        let httpHeaders: any = headers ? headers : {};
        httpHeaders['appVersion'] = _.get(httpHeaders, 'appVersion', this.getAppVersion());
        httpHeaders['user-agent'] = this.getAppVersion();
        httpHeaders['userId'] = _.get(httpHeaders, 'userId', appClient.userId);

        // append client ID to query parameters
        let queryParams: any = {};
        queryParams[QUERY_PARAM_SESSION_ID] = getSessionId();

        const response = await client.request({
            path: url,
            method: 'DELETE',
            params: queryParams,
            headers: httpHeaders,
        }).input();

        if (!response) {
            console.error(response);
            throw new Error(`Received no valid response from backend`);
        }

        return response === BACKEND_RESPONSE_OK;
    }

    public async fetchModelConfigurationFileAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(getModelCustomizationUrl, appClient, undefined, 25);
    }

    public async saveModelConfigurationAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.postJson(saveModelCustomizationUrl, appClient, undefined, json);
    }

    public async deleteModelConfigurationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.delete(deleteModelCustomizationUrl, appClient);
    }

    public async fetchLicenseStatusAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(getLicenseUrl, appClient);
    }

    public async fetchAllDaemonConfigsAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(getAllDaemonConfigsUrl, appClient);
    }

    public async fetchDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(getDaemonConfigUrl, appClient, { daemonSessionId: daemonSessionId });
    }

    public async saveDaemonConfigAsync(appClient: MVisionAppClient | undefined, json: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.postJson(saveDaemonConfigUrl, appClient, undefined, json);
    }

    public async resetDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.postJson(resetDaemonConfigUrl, appClient, { daemonSessionId: daemonSessionId });
    }

    public async deleteDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonSessionId: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.delete(deleteDaemonConfigUrl, appClient, { daemonSessionId: daemonSessionId });
    }

    public async saveLicenseAsync(appClient: MVisionAppClient | undefined, newLicense: string): Promise<boolean> {
        this.checkAppClientIsValid(appClient);

        return await this.postJson(saveLicenseUrl, appClient, undefined, newLicense);
    }

    public async fetchLabelingAsync(): Promise<any> {
        return await this.getAndParseJsonResponse(getLabelingUrl, undefined);
    }

    public async fetchClientListAsync(): Promise<any> {
        return await this.getAndParseJsonResponse(getClientListUrl, undefined, undefined, 25);
    }

    public async fetchActiveModelsAsync(appClient: MVisionAppClient | undefined): Promise<any> {
        this.checkAppClientIsValid(appClient);

        return await this.getAndParseJsonResponse(getListActiveModelsUrl, appClient);
    }


    // returns the "environment" variable from manifest.json, or a default if it's not set
    public getEnvironmentTag(): string {
        return _.get(cockpitManifest, 'environment', defaultEnvironmentTag);
    }

    getAppVersion(): string {
        return `${getDefaultAppName()}/${DISPLAY_VERSION}/LOCAL`;
    }

    getAppWrapperComponent(): (props: React.PropsWithChildren<{}>) => JSX.Element {
        return NoAuthPage;
    }

    checkAppClientIsValid(appClient: MVisionAppClient | undefined): asserts appClient is MVisionAppClient {
        if (appClient === undefined) {
            throw new Error('AppClient was undefined.');
        }
    }
}
